import { createContext, useContext, useEffect, useState, useCallback } from "react";

// UI
import { useToast } from "../ui/use-toast";

// Third Party
import { io, Socket } from "socket.io-client";
import { CHAT_ENDPOINT } from "../constants";
import useUserToken from "@/hook/useUserToken";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { updateMessageRead } from "@/services/chat";
import ObjectId from "bson-objectid";


interface ChatSocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    emitTyping: (conversationId: string, isTyping: boolean) => void;
    emitReadMessage: (conversationId: string) => Promise<void>;
    sendSocketMessage: (type: string, payload: any) => void;
    emitNewMessage: (type: "PRIVATE_NEW_MESSAGE" | "GROUP_NEW_MESSAGE", messageData: {
        conversationId: string;
        messageType: string;
        content: string;
        senderId: string;
        receiverId?: string;
        attachments?: any[];
    }) => boolean;
}

const ChatSocketContext = createContext<ChatSocketContextType | null>(null);

export function ChatSocketProvider({ children }: { children: React.ReactNode }) {
    const { toast } = useToast();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const user = useUserToken()?.data;
    const queryClient = useQueryClient();

    const [reconnectAttempts, setReconnectAttempts] = useState(0);

    const initializeSocket = useCallback(() => {
        if (user?.userChatId) {
            const socketInstance = io(CHAT_ENDPOINT, {
                reconnection: true,
                reconnectionAttempts: Number.POSITIVE_INFINITY,
                reconnectionDelay: 1000,
                timeout: 20000,
            });

            setSocket(socketInstance);
            return socketInstance;
        }

        return null;
    }, [user?.userChatId]);

    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            toast({
                title: "ເຊື່ອມຕໍ່ສຳເລັດ",
                description: "ອອນລາຍ",
            });
            if (!socket?.connected) {
                const newSocket = initializeSocket();
                setSocket(newSocket);
            }
        };

        const handleOffline = () => {
            toast({
                title: "ການເຊື່ອມຕໍ່ຂັດຂ້ອງ",
                description: "ອອຟລາຍ",
                variant: "destructive",
            });
            setIsConnected(false);
        };

        // Handle visibility change (sleep/wake)
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                if (!socket?.connected) {
                    const newSocket = initializeSocket();
                    setSocket(newSocket);
                }
            }
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [socket, initializeSocket, toast]);

    useEffect(() => {
        const socketInstance = initializeSocket();

        if (socketInstance) {
            socketInstance.on("connect", () => {
                setIsConnected(true);
                setReconnectAttempts(0);
                socketInstance.emit("SETUP", {
                    type: "SETUP",
                    payload: { userId: user?.userChatId },
                });
            });

            socketInstance.on("connect_error", () => {
                setIsConnected(false);
                setReconnectAttempts((prev) => prev + 1);
            });

            socketInstance.on("disconnect", () => {
                setIsConnected(false);
                // Attempt to reconnect after disconnect
                setTimeout(() => {
                    if (!socketInstance.connected) {
                        socketInstance.connect();
                    }
                }, 3000);
            });

            socketInstance.on("CONVERSATION_LISTENING", (data) => {
                handleSocketEvents(data, queryClient);
            });
        }

        return () => {
            socketInstance?.disconnect();
        };
    }, [user?.userChatId, queryClient, initializeSocket]);

    // Function to handle socket events

    const sendSocketMessage = useCallback((eventType: string, type: string, payload: any) => {
        if (socket && isConnected) {
            socket.emit(eventType, {
                type,
                payload,
            });
            return true; // Return success status
        } else {
            console.warn("Socket not connected. Cannot send message.");
            return false; // Return failure status
        }
    }, [socket, isConnected]);

    const emitTyping = (conversationId: string, isTyping: boolean) => {
        socket?.emit("TYPING", {
            type: "TYPING",
            payload: {
                conversationId,
                userId: user?.userChatId,
                isTyping,
            },
        });
    };

    const emitReadMessage = async (conversationId: string) => {
        try {
            const response = await updateMessageRead({
                conversationId,
                userId: user?.userChatId,
            });

            // Invalidate queries after successful read status update
            queryClient.invalidateQueries({ queryKey: ["conversationList"] });
            queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
        } catch (error) {
            console.error("Error marking messages as read:", error);
        }
    };

    const emitNewMessage = useCallback((type: "PRIVATE_NEW_MESSAGE" | "GROUP_NEW_MESSAGE", messageData: {
        conversationId: string;
        messageType: string;
        content: string;
        senderId: string;
        receiverId?: string;
        attachments?: Array<{ fileName: string; fileUrl: string }>;
        location?: { lng: number; lat: number };
    }) => {
        console.log("Message Type", type);

        // Adjust content based on messageType
        let payload = {
            ...messageData,
            _id: new ObjectId().toString(), // Generate temporary ID
        };

        // For non-TEXT message types, set content to match messageType
        if (messageData.messageType !== "TEXT") {
            payload.content = messageData.messageType;
        }

        return sendSocketMessage("NEW_MESSAGE", type, payload);
    }, [sendSocketMessage]);

    return (
        <ChatSocketContext.Provider
            value={{
                socket,
                isConnected,
                emitTyping,
                emitReadMessage,
                sendSocketMessage,
                emitNewMessage, // Add the new method to context
            }}
        >
            {children}
        </ChatSocketContext.Provider>
    );
}

export function useChatSocket() {
    const context = useContext(ChatSocketContext);
    if (!context) {
        throw new Error("useChatSocket must be used within ChatSocketProvider");
    }
    return context;
}

function handleSocketEvents(data: any, queryClient: any) {
    const eventHandlers = {
        SETUP: () => {
            queryClient.invalidateQueries({ queryKey: ["conversationList"] });
        },
        NEW_MESSAGE: () => {
            console.log("Data", data?.response)

            queryClient.invalidateQueries({ queryKey: ["conversationList"] });

            queryClient.invalidateQueries({
                queryKey: ["messages", data?.response?.conversation],
            });
        },
        USER_ONLINE: () => {
            queryClient.invalidateQueries({ queryKey: ["conversationList"] });
        },
        TYPING: () => {
            // Handle typing indicator updates
            const { conversationId, userId, isTyping } = data.response;
            // Update UI for typing status
        },
        READ_MESSAGE: () => {

            queryClient.invalidateQueries({ queryKey: ["conversationList"] });
            queryClient.invalidateQueries({
                queryKey: ["messages", data?.response?.conversation],
            });
        },
    };

    const handler = eventHandlers[data.type];
    if (handler) {
        handler();
    }
}